import { useState, type ReactNode } from 'react';
import { ListTree } from 'lucide-react';
import type { FactorySubtasks, SubtaskDecision } from '@shared/factory-subtasks';
import { StatusBadge } from '@/components/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { FoldingCard } from './FoldingCard';
import type { ConcernTags } from '@shared/decision-concern';
import { ConcernBadge } from './ConcernBadge';

/**
 * Sub-tasks the AI builder proposed for its task (a `factory-subtasks` block in its report), as a card.
 * Every one starts ticked; someone who can edit the task unticks any they don't want and adds the rest
 * in one go (the others are left out), or Reject all leaves every one out (SF-T-178). Once decided, each
 * shows Added with its ref, or Left out. Without `onAdd` (a preview) it only lists them.
 */
export function FactorySubtasksCard({
  subtasks,
  decided,
  meta,
  onAdd,
  fold = false,
  id,
  onUntickedChange,
  concerns,
}: {
  subtasks: FactorySubtasks;
  /** What was added, by number (1-based); null or left out while undecided. */
  decided?: ReadonlyMap<number, SubtaskDecision> | null;
  /** Which run and when, at the end of the header line. */
  meta?: ReactNode;
  /** Adds the ticked ones (their numbers); resolves true when they were added. */
  onAdd?: (numbers: number[]) => Promise<boolean>;
  /** Starts folded to its header line, which opens it (SF-T-178: a decided proposal in the task's Proposed sub-tasks). */
  fold?: boolean;
  /** For a pointer in the comments to scroll to it (subtaskProposalId). */
  id?: string;
  /** The numbers unticked so far, on each change (a dialog asks before losing them: the Inbox's Check). */
  onUntickedChange?: (numbers: number[]) => void;
  /** SF-T-182: Jev's Product or Architecture tags for the items (payloadConcerns of the message), a badge on each. */
  concerns?: ConcernTags;
}) {
  const list = subtasks.subtasks;
  const [unticked, setUnticked] = useState<ReadonlySet<number>>(new Set());
  // Which button is working: Add (the ticked ones) or Reject all.
  const [adding, setAdding] = useState<'add' | 'reject' | null>(null);
  const picked = list.map((_s, i) => i + 1).filter((n) => !unticked.has(n));
  const open = !decided;

  const toggle = (number: number, on: boolean) => {
    const next = new Set(unticked);
    if (on) next.delete(number);
    else next.add(number);
    setUnticked(next);
    onUntickedChange?.([...next]);
  };

  const add = async (which: 'add' | 'reject') => {
    if (!onAdd) return;
    setAdding(which);
    await onAdd(which === 'add' ? picked : []);
    setAdding(null);
  };

  return (
    <FoldingCard
      id={id}
      fold={fold}
      summary={decided ? decidedSummary(list.length, decided) : undefined}
      header={
        <>
          <ListTree className="size-4" aria-hidden />
          {list.length === 1 ? 'A sub-task proposed by the AI builder' : `${list.length} sub-tasks proposed by the AI builder`}
          {open ? null : <Badge variant="secondary">Decided</Badge>}
          {meta ? <span className="ml-auto font-normal">{meta}</span> : null}
        </>
      }
    >
      <ol className="space-y-2.5">
        {list.map((s, i) => {
          const number = i + 1;
          const decision = decided?.get(number);
          return (
            <li key={number} className="flex items-start gap-2">
              {open && onAdd ? (
                <Checkbox
                  className="mt-0.5"
                  checked={!unticked.has(number)}
                  disabled={adding !== null}
                  onCheckedChange={(checked) => toggle(number, checked === true)}
                  aria-label={`Add “${s.title}”`}
                />
              ) : (
                <span className="w-4 shrink-0 text-right text-sm text-muted-foreground">{number}.</span>
              )}
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
                  <p className="min-w-0 flex-1 text-sm font-medium">
                    {s.title} <ConcernBadge concerns={concerns} kind="subtask" item={number} />
                  </p>
                  {decided ? (
                    <StatusBadge tone={decision?.ref ? 'success' : 'neutral'} size="sm" dot>
                      {decision?.ref ? `Added: ${decision.ref}` : 'Left out'}
                    </StatusBadge>
                  ) : null}
                </div>
                {s.detail ? <p className="whitespace-pre-wrap break-words text-xs text-muted-foreground">{s.detail}</p> : null}
              </div>
            </li>
          );
        })}
      </ol>
      {open && onAdd ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <span className="mr-auto text-xs text-muted-foreground">
            {picked.length === list.length ? 'All ticked' : `${picked.length} of ${list.length} ticked`}; the rest are left out
          </span>
          <Button
            size="sm"
            variant="outline"
            loading={adding === 'reject'}
            disabled={adding === 'add'}
            data-hint="Leave every one out; it's recorded on the task"
            onClick={() => void add('reject')}
          >
            Reject all
          </Button>
          {picked.length ? (
            <Button size="sm" loading={adding === 'add'} disabled={adding === 'reject'} onClick={() => void add('add')}>
              {picked.length === 1 ? 'Add 1 sub-task' : `Add ${picked.length} sub-tasks`}
            </Button>
          ) : null}
        </div>
      ) : null}
    </FoldingCard>
  );
}

/** What was decided, on one line for a folded proposal: "Added SF-T-60, SF-T-61 · 1 left out". */
function decidedSummary(total: number, decided: ReadonlyMap<number, SubtaskDecision>): string {
  const refs = [...decided.values()].flatMap((d) => (d.ref ? [d.ref] : []));
  const left = total - refs.length;
  if (!refs.length) return total === 1 ? 'Rejected' : `All ${total} rejected`;
  return [`Added ${refs.join(', ')}`, ...(left > 0 ? [`${left} left out`] : [])].join(' · ');
}

/** In the comments, the AI builder's proposed sub-tasks as one line pointing to the task's Proposed sub-tasks, where they're added. */
export function FactorySubtasksPointer({
  count,
  onShow,
}: {
  count: number;
  /** Scrolls to the proposal (showProposedSubtasks). */
  onShow: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border bg-muted/40 px-2.5 py-1.5 text-xs text-muted-foreground">
      <ListTree className="size-4 shrink-0" aria-hidden />
      <span>{count === 1 ? 'Proposed a sub-task' : `Proposed ${count} sub-tasks`}</span>
      <Button variant="link" size="xs" className="h-auto p-0 text-xs" onClick={onShow}>
        See Proposed sub-tasks
      </Button>
    </div>
  );
}
